import React from "react";
import { Create, useForm } from "@refinedev/antd";
import { useNavigate } from "react-router-dom";
import { 
  Form,
  Input,
  DatePicker,
  Select,
  Checkbox,
  Radio,
  Card,
  Typography,
  Row,
  Col,
  Button,
} from "antd";
import { RollbackOutlined } from "@ant-design/icons";
import { IRaceMaster } from "@/interfaces/race.interface";
import dayjs from "dayjs";

const { Title, Text } = Typography;

interface IRaceMasterForm extends IRaceMaster {
  race_type: 'normal' | 'dragon';
}

export const RaceCreate: React.FC = () => {
  const navigate = useNavigate();
  const { formProps, saveButtonProps, onFinish } = useForm<IRaceMasterForm>({
    resource: "race_master",
    redirect: false,
    successNotification: {
      message: "レースデータの作成に成功しました。",
      type: "success",
    },
    onMutationSuccess: (data) => {
      navigate(`/races/show/${data.data.id}`);
    },
  });

  const handleSubmit = async (values: IRaceMasterForm) => {
    const { race_type, race_date, deadline_date, publication_date_time, ...rest } = values;
    
    const formattedValues = {
      ...rest,
      race_date: race_date ? dayjs(race_date).add(9, 'hour').format('YYYY-MM-DD') : undefined,
      deadline_date: deadline_date ? dayjs(deadline_date).add(9, 'hour').format('YYYY-MM-DD') : undefined,
      publication_date_time: publication_date_time ? dayjs(publication_date_time).subtract(9, 'hour').format('YYYY-MM-DD HH:mm:00') : undefined, // UTC+9（日本時間）
      is_point_race: false,
      is_dragon: race_type === 'dragon',
    };
    
    await onFinish(formattedValues);
  };

  return (
    <div className="max-w-5xl mx-auto">
      <Create
        saveButtonProps={saveButtonProps}
        headerButtons={({ defaultButtons }) => (
          <>
            {defaultButtons}
            <Button onClick={() => navigate("/races")} icon={<RollbackOutlined />}>
              キャンセル
            </Button>
          </>
        )}
      >
        <Card>
          <div className="space-y-6">
            <div className="space-y-4">
              <Title level={2}>レース作成</Title>
              <Text type="secondary">新しいレースの詳細情報を入力してください</Text>
            </div>

            <Form 
              {...formProps} 
              layout="vertical" 
              onFinish={(values) => handleSubmit(values as any)}
            >
              <Row gutter={[16, 16]}>
                <Col span={24}>
                  <Form.Item
                    label="レース名"
                    name="name"
                    rules={[{ required: true, message: 'レース名を入力してください' }]}
                  >
                    <Input placeholder="レース名を入力" />
                  </Form.Item>
                </Col>

                <Col span={24}>
                  <Card size="small" className="bg-gray-50">
                    <Row gutter={[16, 16]}>
                      <Col span={24}>
                        <Form.Item
                          name="race_type"
                          label="レースタイプ"
                        >
                          <Radio.Group defaultValue="normal">
                            <Radio value="normal">通常レース</Radio>
                            <Radio value="dragon">ドラゴン</Radio>
                          </Radio.Group>
                        </Form.Item>
                      </Col>
                      <Col span={24}>
                        <Form.Item
                          valuePropName="checked"
                          name="is_multiple"
                          label="開催形式"
                        >
                          <Checkbox>複数日開催</Checkbox>
                        </Form.Item>
                      </Col>
                    </Row>
                  </Card>
                </Col>

                <Col xs={24} md={12}>
                  <Form.Item
                    label="開催日"
                    name="race_date"
                    rules={[{ required: true, message: '開催日を選択してください' }]}
                  >
                    <DatePicker
                      style={{ width: '100%' }}
                      placeholder="日付を選択"
                      format="YYYY年MM月DD日"
                      showToday
                    />
                  </Form.Item>
                </Col>

                <Col xs={24} md={12}>
                  <Form.Item
                    label="エントリー締切日"
                    name="deadline_date"
                    rules={[{ required: true, message: '締切日を選択してください' }]}
                  >
                    <DatePicker
                      style={{ width: '100%' }}
                      placeholder="日付を選択"
                      format="YYYY年MM月DD日"
                      showToday
                    />
                  </Form.Item>
                </Col>

                <Col span={24}>
                  <Form.Item
                    label="公開日時"
                    name="publication_date_time"
                    rules={[{ required: true, message: '公開日時を選択してください' }]}
                  >
                    <DatePicker
                      showTime
                      style={{ width: '100%' }}
                      placeholder="日時を選択"
                      format="YYYY-MM-DD HH:mm"
                      showToday
                    />
                  </Form.Item>
                </Col>

                <Col xs={24} md={12}>
                  <Form.Item
                    label="公開ステータス"
                    name="publication_status"
                    initialValue="draft"
                  >
                    <Select>
                      <Select.Option value="draft">下書き</Select.Option>
                      <Select.Option value="published">公開中</Select.Option>
                    </Select>
                  </Form.Item>
                </Col>
              </Row>
            </Form>
          </div>
        </Card>
      </Create>
    </div>
  );
};